import {ref, readonly} from 'vue'
import {supabase} from '@/lib/supabase'
import type {User} from '@supabase/supabase-js'

const user = ref<User | null>(null)
const profile = ref<Profile | null>(null)
const loading = ref(true)

// Profile Type
export interface Profile {
    id: string              // Username (z.B. "beckm")
    auth_id: string         // Supabase Auth UUID
    email: string
    full_name?: string
    work_group: 'TDE-PM3' | 'TDM-PM3' | 'PROD-PM3'
    role: 'observer' | 'user' | 'mod' | 'admin'
    created_at?: string
    updated_at?: string
}

// Registration Data Type
export interface RegistrationData {
    username: string        // Pflichtfeld - wird zu profile.id
    work_group: 'TDE-PM3' | 'TDM-PM3' | 'PROD-PM3'  // Pflichtfeld
    full_name?: string
}

// Session beim App-Start laden (non-blocking) 99101
supabase.auth.getSession().then(({data}) => {
    user.value = data.session?.user ?? null
    if (user.value) {
        // Nicht awaiten - im Hintergrund laden
        loadProfile(user.value.id).catch(console.error)
    }
    loading.value = false
})

// Auf Auth-Änderungen reagieren (non-blocking!)
supabase.auth.onAuthStateChange((event, session) => {
    //console.log('Auth state changed:', event)
    user.value = session?.user ?? null
    if (session?.user) {
        // WICHTIG: Nicht awaiten! Sonst blockiert alles
        loadProfile(session.user.id).catch(console.error)
    } else {
        profile.value = null
    }
})

// Profil laden
async function loadProfile(authId: string): Promise<Profile | null> {
    const {data, error} = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_id', authId)
        .maybeSingle()  // Gibt null zurück statt Error bei 0 Zeilen

    if (error) {
        //console.error('Failed to load profile:', error)
        profile.value = null
        return null
    }

    if (!data) {
        //console.warn('No profile found for auth_id:', authId)
        profile.value = null
        return null
    }

    profile.value = data
    return data
}

export function useAuth() {
    // Login mit E-Mail oder Username
    async function signIn(emailOrUsername: string, password: string) {
        let email = emailOrUsername

        // Prüfen ob es eine E-Mail ist (enthält @)
        const isEmail = emailOrUsername.includes('@')

        if (!isEmail) {
            // Username → E-Mail aus Profil holen
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('email')
                .eq('id', emailOrUsername.toLowerCase())
                .maybeSingle()

            if (profileError) {
                throw new Error(`Fehler beim Suchen des Benutzers: ${profileError.message}`)
            }

            if (!profileData || !profileData.email) {
                throw new Error('Benutzer nicht gefunden')
            }

            email = profileData.email
        }

        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        })
        if (error) throw error
        user.value = data.user

        // Profil laden
        if (data.user) {
            await loadProfile(data.user.id)
        }

        return data.user
    }

    // Registrierung mit Profil-Erstellung
    async function signUp(email: string, password: string, registrationData: RegistrationData) {
        // Validierung
        if (!registrationData.username || registrationData.username.trim() === '') {
            throw new Error('Username ist erforderlich')
        }
        if (!registrationData.work_group) {
            throw new Error('Arbeitsgruppe ist erforderlich')
        }

        const username = registrationData.username.trim().toLowerCase()
        //console.log('Starting registration for:', username)

        // Username-Duplikat wird durch DB-Constraint (PRIMARY KEY) abgefangen
        // Der Fehler kommt dann beim INSERT unten

        // 1. Auth User erstellen mit Metadaten
        const { data: authData, error: authError } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    username: username,
                    full_name: registrationData.full_name,
                    work_group: registrationData.work_group
                }
            }
        })

        if (authError) throw authError
        if (!authData.user) throw new Error('Registrierung fehlgeschlagen')

        //console.log('signUp success:', authData.user.id)

        // 2. Profil erstellen oder bestehendes Placeholder-Profil (auth_id IS NULL) übernehmen
        // Check if a placeholder profile already exists for this username
        const { data: existingProfile } = await supabase
            .from('profiles')
            .select('id, auth_id, full_name')
            .eq('id', username)
            .maybeSingle()

        let profileError: { message: string } | null = null

        if (existingProfile && existingProfile.auth_id === null) {
            // Placeholder exists → claim it by setting auth_id + updating fields
            const { error } = await supabase
                .from('profiles')
                .update({
                    auth_id: authData.user.id,
                    email: email,
                    full_name: registrationData.full_name || existingProfile.full_name || null,
                    work_group: registrationData.work_group,
                })
                .eq('id', username)
            profileError = error
        } else {
            // No placeholder → normal insert
            const { error } = await supabase
                .from('profiles')
                .insert({
                    id: username,
                    auth_id: authData.user.id,
                    email: email,
                    full_name: registrationData.full_name || null,
                    work_group: registrationData.work_group,
                    role: 'user'
                })
            profileError = error
        }

        if (profileError) {
            throw new Error(`Profil konnte nicht erstellt werden: ${profileError.message}`)
        }

        //console.log('Registration complete!')
        return authData.user
    }

    // Logout
    async function signOut() {
        const {error} = await supabase.auth.signOut()
        if (error) throw error
        user.value = null
        profile.value = null
    }

    // Passwort zurücksetzen
    async function resetPassword(email: string) {
        const {error} = await supabase.auth.resetPasswordForEmail(email)
        if (error) throw error
    }

    // Aktuelles Profil neu laden
    async function refreshProfile() {
        if (user.value) {
            await loadProfile(user.value.id)
        }
    }

    return {
        user: readonly(user),
        profile: readonly(profile),
        loading: readonly(loading),
        isAuthenticated: () => !!user.value,
        getUsername: () => profile.value?.id ?? null,
        getWorkGroup: () => profile.value?.work_group ?? null,
        getRole: () => profile.value?.role ?? 'observer',
        signIn,
        signUp,
        signOut,
        resetPassword,
        refreshProfile
    }
}
